import { supabase } from '../lib/supabase';
import { toCamelCase, toSnakeCase } from './supabaseService';

// Helper to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Get user ID with dev mode support
const getUserId = async () => {
  const isDevMode = import.meta.env?.VITE_DEV_MODE === 'true';
  if (isDevMode) {
    return '00000000-0000-0000-0000-000000000001';
  }
  const { data: { user } } = await supabase?.auth?.getUser();
  return user?.id;
};

// ============================================
// TRANSACTIONS
// ============================================

export const getTransactions = async (filters = {}) => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    let query = supabase
      ?.from('transactions')
      ?.select(`
        *,
        job:jobs(id, title)
      `)
      ?.eq('user_id', userId);

    // Apply filters
    if (filters?.type) {
      query = query?.eq('type', filters?.type);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.startDate) {
      query = query?.gte('created_at', filters?.startDate);
    }
    if (filters?.endDate) {
      query = query?.lte('created_at', filters?.endDate);
    }
    if (filters?.minAmount) {
      query = query?.gte('amount', filters?.minAmount);
    }
    if (filters?.maxAmount) {
      query = query?.lte('amount', filters?.maxAmount);
    }

    // Apply sorting and pagination
    query = query?.order('created_at', { ascending: false });
    
    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.error('Transactions fetch error:', error?.message);
      return [];
    }

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Get transactions error:', error);
    throw error;
  }
};

export const getTransactionById = async (transactionId) => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    const { data, error } = await supabase
      ?.from('transactions')
      ?.select(`
        *,
        job:jobs(id, title, category)
      `)
      ?.eq('id', transactionId)
      ?.eq('user_id', userId)
      ?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('Transaction fetch error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Get transaction by ID error:', error);
    throw error;
  }
};

export const createTransaction = async (transactionData) => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    const transactionPayload = toSnakeCase({
      ...transactionData,
      userId
    });

    const { data, error } = await supabase
      ?.from('transactions')
      ?.insert(transactionPayload)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('Transaction creation error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create transaction error:', error);
    throw error;
  }
};

// ============================================
// WITHDRAWAL METHODS
// ============================================

export const getWithdrawalMethods = async () => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    const { data, error } = await supabase
      ?.from('withdrawal_methods')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.order('is_primary', { ascending: false })
      ?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.error('Withdrawal methods fetch error:', error?.message);
      return [];
    }

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Get withdrawal methods error:', error);
    throw error;
  }
};

export const createWithdrawalMethod = async (methodData) => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    const methodPayload = toSnakeCase({
      ...methodData,
      userId
    });

    const { data, error } = await supabase
      ?.from('withdrawal_methods')
      ?.insert(methodPayload)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('Withdrawal method creation error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Create withdrawal method error:', error);
    throw error;
  }
};

export const updateWithdrawalMethod = async (methodId, updates) => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    const updatePayload = toSnakeCase(updates);

    const { data, error } = await supabase
      ?.from('withdrawal_methods')
      ?.update(updatePayload)
      ?.eq('id', methodId)
      ?.eq('user_id', userId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('Withdrawal method update error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Update withdrawal method error:', error);
    throw error;
  }
};

export const deleteWithdrawalMethod = async (methodId) => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    const { error } = await supabase
      ?.from('withdrawal_methods')
      ?.delete()
      ?.eq('id', methodId)
      ?.eq('user_id', userId);

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('Withdrawal method deletion error:', error?.message);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Delete withdrawal method error:', error);
    throw error;
  }
};

export const setPrimaryWithdrawalMethod = async (methodId) => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    // First, set all methods to non-primary
    await supabase
      ?.from('withdrawal_methods')
      ?.update({ is_primary: false })
      ?.eq('user_id', userId);

    // Then set the selected method as primary
    const { data, error } = await supabase
      ?.from('withdrawal_methods')
      ?.update({ is_primary: true })
      ?.eq('id', methodId)
      ?.eq('user_id', userId)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('Set primary method error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Set primary withdrawal method error:', error);
    throw error;
  }
};

// ============================================
// PAYOUTS
// ============================================

export const getPayouts = async (filters = {}) => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    let query = supabase
      ?.from('payouts')
      ?.select(`
        *,
        withdrawal_method:withdrawal_methods(*)
      `)
      ?.eq('user_id', userId);

    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }

    query = query?.order('requested_at', { ascending: false });

    if (filters?.limit) {
      query = query?.limit(filters?.limit);
    }

    const { data, error } = await query;

    if (error) {
      if (isSchemaError(error)) throw error;
      if (error?.code === 'PGRST116') return [];
      console.error('Payouts fetch error:', error?.message);
      return [];
    }

    return toCamelCase(data || []);
  } catch (error) {
    console.error('Get payouts error:', error);
    throw error;
  }
};

export const requestPayout = async (payoutData) => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    const payoutPayload = toSnakeCase({
      ...payoutData,
      userId,
      status: 'pending'
    });

    const { data, error } = await supabase
      ?.from('payouts')
      ?.insert(payoutPayload)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('Payout request error:', error?.message);
      return null;
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Request payout error:', error);
    throw error;
  }
};

// ============================================
// ANALYTICS
// ============================================

export const getEarningsSummary = async (year = new Date()?.getFullYear()) => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    // Get transactions for the year
    const { data: transactions, error: transError } = await supabase
      ?.from('transactions')
      ?.select('amount, type, created_at')
      ?.eq('user_id', userId)
      ?.eq('status', 'completed')
      ?.gte('created_at', `${year}-01-01`)
      ?.lte('created_at', `${year}-12-31`);

    if (transError) {
      if (isSchemaError(transError)) throw transError;
      console.error('Earnings summary fetch error:', transError?.message);
      return { totalEarnings: 0, totalWithdrawals: 0, currentBalance: 0, monthlyData: [] };
    }

    const earnings = transactions?.filter(t => t?.type === 'earning' || t?.type === 'bonus') || [];
    const withdrawals = transactions?.filter(t => t?.type === 'withdrawal') || [];

    const totalEarnings = earnings?.reduce((sum, t) => sum + parseFloat(t?.amount || 0), 0);
    const totalWithdrawals = withdrawals?.reduce((sum, t) => sum + parseFloat(t?.amount || 0), 0);
    const currentBalance = totalEarnings - totalWithdrawals;

    // Monthly breakdown
    const monthlyData = Array?.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      earnings: 0,
      withdrawals: 0
    }));

    transactions?.forEach(t => {
      const month = new Date(t?.created_at)?.getMonth();
      const amount = parseFloat(t?.amount || 0);
      if (t?.type === 'earning' || t?.type === 'bonus') {
        monthlyData[month].earnings += amount;
      } else if (t?.type === 'withdrawal') {
        monthlyData[month].withdrawals += amount;
      }
    });

    return {
      totalEarnings,
      totalWithdrawals,
      currentBalance,
      monthlyData
    };
  } catch (error) {
    console.error('Get earnings summary error:', error);
    throw error;
  }
};

export const getPayoutSchedule = async () => {
  try {
    const userId = await getUserId();
    if (!userId) throw new Error('User not authenticated');

    // Get pending payouts
    const { data: pendingPayouts, error } = await supabase
      ?.from('payouts')
      ?.select('*')
      ?.eq('user_id', userId)
      ?.in('status', ['pending', 'processing'])
      ?.order('requested_at', { ascending: true });

    if (error) {
      if (isSchemaError(error)) throw error;
      console.error('Payout schedule fetch error:', error?.message);
      return { nextPayout: null, pendingAmount: 0 };
    }

    const nextPayout = pendingPayouts?.[0] || null;
    const pendingAmount = pendingPayouts?.reduce((sum, p) => sum + parseFloat(p?.amount || 0), 0) || 0;

    return {
      nextPayout: nextPayout ? toCamelCase(nextPayout) : null,
      pendingAmount
    };
  } catch (error) {
    console.error('Get payout schedule error:', error);
    throw error;
  }
};